import React from "react";
import ReactMarkdown from "react-markdown";
import BottomNavBar from "../components/layout/BottomNav";
import Header from "../components/layout/Header";
import { useDayPostList } from "../hooks/useDayPostData";
import { useAuth } from "../contexts/AuthContext";
import { useLocation } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/config";
import { generateDateID } from "../services/dayPostService";
import { UserPrivateData } from "../types/user";
import { getCycleLogs, calculateCycleStats } from "../services/cycleService";
import { CyclePhase, CycleStats } from "../types/cycle";

// Cycle phase display names and colors
const CYCLE_PHASE_DISPLAY: Record<
  string,
  { name: string; color: string; bgColor: string }
> = {
  menstruation: {
    name: "Menstruation",
    color: "text-red-700 dark:text-red-400",
    bgColor: "bg-red-500/15",
  },
  early_follicular: {
    name: "Early Follicular",
    color: "text-orange-700 dark:text-orange-400",
    bgColor: "bg-orange-500/15",
  },
  late_follicular: {
    name: "Late Follicular",
    color: "text-yellow-700 dark:text-yellow-400",
    bgColor: "bg-yellow-500/15",
  },
  ovulatory: {
    name: "Ovulatory",
    color: "text-green-700 dark:text-green-400",
    bgColor: "bg-green-500/15",
  },
  early_luteal: {
    name: "Early Luteal",
    color: "text-pink-700 dark:text-pink-400",
    bgColor: "bg-pink-500/15",
  },
  late_luteal: {
    name: "Late Luteal",
    color: "text-purple-700 dark:text-purple-400",
    bgColor: "bg-purple-500/15",
  },
};

// Determine cycle phase from cycle day and average cycle length
const determineCyclePhase = (
  cycleDay: number,
  averageCycleLength: number,
): CyclePhase => {
  const adjusted = averageCycleLength > 0 ? averageCycleLength : 28;
  const progress = (cycleDay / adjusted) * 100;
  if (progress <= 15) return "menstruation";
  if (progress <= 30) return "early_follicular";
  if (progress <= 45) return "late_follicular";
  if (progress <= 55) return "ovulatory";
  if (progress <= 75) return "early_luteal";
  return "late_luteal";
};

const DailyReport = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const queryUid = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("uid") || params.get("UID");
  }, [location.search]);

  // Use 28 days of data for comprehensive trigger analysis
  // Disable the hook's automatic data loading since we'll load 28 days manually
  const {
    dayPosts,
    loading,
    error,
    isFromCache,
    isOffline,
    lastUpdated,
    fetchDayPostsData,
  } = useDayPostList({
    userId: queryUid || undefined,
    disableCache: !!queryUid,
    disableAutoLoad: true,
  });

  const effectiveUserId = queryUid || currentUser?.uid;

  // Fetch 28 days of data on component mount (only once)
  const [hasInitialLoad, setHasInitialLoad] = React.useState(false);
  const [simpleReport, setSimpleReport] = React.useState(false);
  const [reportMarkdown, setReportMarkdown] = React.useState<string | null>(
    null,
  );
  const [reportLoading, setReportLoading] = React.useState(false);
  const [isFemale, setIsFemale] = React.useState(false);
  const [cycleDay, setCycleDay] = React.useState<number | null>(null);
  const [cyclePhase, setCyclePhase] = React.useState<CyclePhase>(null);
  const [cycleStats, setCycleStats] = React.useState<CycleStats | null>(null);

  React.useEffect(() => {
    setHasInitialLoad(false);
  }, [effectiveUserId]);

  React.useEffect(() => {
    if (effectiveUserId && !hasInitialLoad) {
      fetchDayPostsData(false, 28); // Fetch 28 days of data
      setHasInitialLoad(true);
    }
  }, [effectiveUserId, hasInitialLoad, fetchDayPostsData]); // Only run once per user

  // Build the daily report markdown server-side (single source of truth shared
  // across all client platforms). The report builder, prompt and AI-insight
  // lookups all live in the buildDailyReport Firebase Function.
  React.useEffect(() => {
    if (!effectiveUserId) return;

    let cancelled = false;

    const fetchReport = async () => {
      setReportLoading(true);
      try {
        const buildDailyReport = httpsCallable<
          {
            uid?: string;
            simple: boolean;
            cyclePhase: CyclePhase;
            isFemale: boolean;
            dateID: string;
          },
          { markdown: string | null }
        >(functions, "buildDailyReport");

        const { data } = await buildDailyReport({
          uid: queryUid || undefined,
          simple: simpleReport,
          cyclePhase,
          isFemale,
          dateID: generateDateID(new Date()),
        });

        if (!cancelled) {
          setReportMarkdown(data.markdown);
        }
      } catch (err) {
        console.error("Failed to build daily report:", err);
        if (!cancelled) {
          setReportMarkdown(null);
        }
      } finally {
        if (!cancelled) {
          setReportLoading(false);
        }
      }
    };

    fetchReport();

    return () => {
      cancelled = true;
    };
  }, [effectiveUserId, queryUid, simpleReport, cyclePhase, isFemale]);

  // Fetch gender and cycle data for female athletes
  React.useEffect(() => {
    if (!effectiveUserId) return;

    const fetchGenderAndCycle = async () => {
      try {
        // Fetch gender from private data
        const privateRef = doc(
          db,
          "users",
          effectiveUserId,
          "private",
          "data",
        );
        const privateDoc = await getDoc(privateRef);
        if (!privateDoc.exists()) return;

        const privateData = privateDoc.data() as UserPrivateData;
        const gender = privateData?.gender?.toLowerCase();

        if (gender === "female") {
          setIsFemale(true);

          // Fetch cycle logs and calculate stats
          const logs = await getCycleLogs(effectiveUserId);
          if (logs.length > 0) {
            const stats = calculateCycleStats(logs);
            setCycleStats(stats);

            if (stats?.most_recent_cycle_start_date) {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const lastStart = new Date(stats.most_recent_cycle_start_date);
              lastStart.setHours(0, 0, 0, 0);
              const daysSinceStart = Math.floor(
                (today.getTime() - lastStart.getTime()) / (1000 * 60 * 60 * 24),
              );
              const day = daysSinceStart + 1; // Day 1 is first day
              if (day >= 1) {
                setCycleDay(day);
                setCyclePhase(
                  determineCyclePhase(day, stats.average_cycle_length),
                );
              }
            }
          }
        } else {
          setIsFemale(false);
          setCycleDay(null);
          setCyclePhase(null);
          setCycleStats(null);
        }
      } catch (error) {
        console.warn("Failed to fetch gender/cycle data:", error);
      }
    };

    fetchGenderAndCycle();
  }, [effectiveUserId]);

  // Get only the most recent 7 days for the top report section
  const recent7Days = React.useMemo(() => {
    return dayPosts
      .sort((a, b) => b.dateID.localeCompare(a.dateID)) // Sort by date descending
      .slice(0, 7); // Take only the most recent 7 days
  }, [dayPosts]);

  return (
    <div className="w-full pt-16 pb-16 dashboard-background">
      <Header />
      {/* Header */}
      <div className="">
        <div className="mx-auto max-w-4xl px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center px-4 pt-4">
              <div>
                <h1 className="text-4xl font-heading font-semibold text-base-content">
                  Daily Report
                </h1>
                <p className="text-sm font-grotesk text-base-content/70 mt-2">
                  Your comprehensive daily summary and analytics
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto space-y-4 px-4">
        {/* Status indicators */}
        {isOffline && (
          <div className="bg-orange-500/10 border-orange-500/30 border p-3 rounded-md">
            <span className="text-orange-700 dark:text-orange-400 text-sm font-medium">
              📱 You're currently offline. Showing cached data.
            </span>
          </div>
        )}

        {isFromCache && !isOffline && (
          <div className="bg-blue-500/10 border-blue-500/30 border p-3 rounded-md">
            <span className="text-blue-700 dark:text-blue-300 text-sm font-medium">
              💾 Showing cached data from {lastUpdated?.toLocaleString()}
            </span>
          </div>
        )}

        {/* Data Display */}
        <div className="bg-base-100 p-6 rounded-md shadow-lg">
          <div className="mb-4">
            <h2 className="text-2xl font-heading font-semibold text-base-content/80 mb-2">
              Daily Report & Analysis
            </h2>
            <p className="text-base-content/60 font-grotesk text-sm">
              Peak score analysis based on 7 days of data, with AI
              recommendations from 28 days of patterns
            </p>
            <label className="mt-4 flex items-center gap-3 cursor-pointer">
              <span className="text-sm font-grotesk text-base-content/70">
                Simple report
              </span>
              <input
                type="checkbox"
                className="toggle toggle-primary"
                checked={simpleReport}
                onChange={(e) => setSimpleReport(e.target.checked)}
              />
            </label>
          </div>

          {loading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-base-content mx-auto"></div>
              <p className="text-base-content/60 mt-2">Loading data...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-8">
              <p className="text-red-500">
                Error loading data: {error.message}
              </p>
              <button
                onClick={() => fetchDayPostsData(false, 28)}
                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Retry (28 Days)
              </button>
            </div>
          )}

          {!loading && !error && (
            <div className="space-y-4">
              {/* Summary stats */}
              <div className="">
                <h3 className="font-semibold mb-2">Data Summary</h3>
                <div className="text-sm">
                  <div>
                    <span className="text-base-content/70">User ID:</span>
                    <span className="ml-2 font-medium font-mono text-xs">
                      {effectiveUserId || "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="text-base-content/70">
                      Recent Days (Report):
                    </span>
                    <span className="ml-2 font-medium">
                      {recent7Days.length}
                    </span>
                  </div>
                  <div>
                    <span className="text-base-content/70">
                      Total Days (Analysis):
                    </span>
                    <span className="ml-2 font-medium">{dayPosts.length}</span>
                  </div>
                  <div>
                    <span className="text-base-content/70">From Cache:</span>
                    <span className="ml-2 font-medium">
                      {isFromCache ? "Yes" : "No"}
                    </span>
                  </div>
                  <div>
                    <span className="text-base-content/70">Offline:</span>
                    <span className="ml-2 font-medium">
                      {isOffline ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Cycle Phase Info (female athletes only) */}
              {isFemale && cycleDay && cyclePhase && (
                <div
                  className={`mt-4 p-4 rounded-md ${CYCLE_PHASE_DISPLAY[cyclePhase]?.bgColor || "bg-base-200"}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-heading text-base-content/70">
                        Cycle Tracking
                      </h3>
                      <p
                        className={`text-lg font-semibold font-grotesk ${CYCLE_PHASE_DISPLAY[cyclePhase]?.color || "text-base-content"}`}
                      >
                        {CYCLE_PHASE_DISPLAY[cyclePhase]?.name || cyclePhase}{" "}
                        <span className="text-base-content/60 font-light">
                          phase
                        </span>
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-base-content/80 text-md">
                        Day{" "}
                        <strong className="text-base bg-pink-500/15 rounded-full px-2 py-0.5">
                          {cycleDay}
                        </strong>
                      </p>
                      {cycleStats?.average_cycle_length && (
                        <p className="text-xs text-base-content/50 mt-1">
                          of ~{cycleStats.average_cycle_length} day cycle
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Peak Score Analysis */}
              <div className="mt-8">
                {reportLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-base-content mx-auto"></div>
                    <p className="text-base-content/60 mt-2">
                      Building report...
                    </p>
                  </div>
                ) : reportMarkdown ? (
                  <div className="prose prose-lg max-w-none">
                    <ReactMarkdown
                      components={{
                        h1: ({ children }) => (
                          <h1 className="text-2xl font-bold text-blue-700 dark:text-blue-400 mb-4">
                            {children}
                          </h1>
                        ),
                        h2: ({ children }) => (
                          <h2 className="text-lg font-semibold text-blue-700 mb-3 mt-6">
                            {children}
                          </h2>
                        ),
                        p: ({ children }) => (
                          <p className="mb-3 text-base-content/80">{children}</p>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-semibold text-base-content">
                            {children}
                          </strong>
                        ),
                        em: ({ children }) => (
                          <em className="italic text-blue-600 text-base font-medium">
                            {children}
                          </em>
                        ),
                      }}
                    >
                      {reportMarkdown}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-base-content/60 text-center py-8">
                    No report available yet. Log today's wellness and generate
                    your peak score to see your daily report.
                  </p>
                )}
              </div>

              {/* Refresh button */}
              <div className="text-center">
                <button
                  onClick={() => fetchDayPostsData(false, 28)}
                  className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Refresh Data (28 Days)
                </button>
              </div>
            </div>
          )}
        </div>

        <BottomNavBar />
      </div>
    </div>
  );
};

export default DailyReport;
